"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { CheckCircle2, PartyPopper, Sparkles, UtensilsCrossed } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { routes } from "@/lib/routes";

const WHATSAPP_BIRTHDAYS =
  "https://wa.me/5492944636295?text=Hola%20Big%20Sports!%20Quiero%20consultar%20por%20un%20cumpleaños.%20Gracias!";

const features = ["birthdays.feature1", "birthdays.feature2", "birthdays.feature3"] as const;

export default function BirthdaysPage() {
  const { t } = useTranslation();

  return (
    <div className="bg-surface-muted min-h-screen pb-20">
      {/* Hero — layered treatment aligned with pricing page */}
      <section className="relative flex min-h-[240px] flex-col items-center justify-center overflow-hidden px-4 sm:min-h-[280px] md:min-h-[300px]">
        <div className="absolute inset-0 bg-slate-950" aria-hidden />
        <Image
          src="/new/1.jpg"
          alt={t("images.birthdaysHero")}
          fill
          priority
          sizes="100vw"
          className="object-cover object-center opacity-[0.88]"
          quality={85}
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_90%_80%_at_50%_18%,transparent_0%,rgba(15,23,42,0.88)_62%,rgb(15,23,42)_100%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-linear-to-b from-slate-950/25 via-slate-950/65 to-slate-950"
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-3xl px-2 pt-6 text-center sm:pt-8">
          <p className="mx-auto mb-5 inline-flex max-w-2xl items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium leading-snug text-white/95 shadow-sm backdrop-blur-md sm:text-sm">
            {t("birthdays.subtitle")}
          </p>
          <h1 className="text-[1.65rem] font-semibold tracking-[-0.02em] text-white sm:text-4xl md:text-[2.65rem] md:leading-tight">
            {t("birthdays.title")}
          </h1>
          <nav
            className="mt-5 flex items-center justify-center gap-2 text-[13px] font-medium text-slate-400"
            aria-label="Breadcrumb"
          >
            <Link href={routes.home} className="transition-colors hover:text-white">
              {t("contact.breadcrumbHome")}
            </Link>
            <span className="text-slate-600" aria-hidden>
              —
            </span>
            <span className="text-slate-200">{t("header.birthdays")}</span>
          </nav>
        </div>
      </section>

      <div className="relative z-20 mx-auto -mt-9 w-full max-w-5xl px-4 sm:-mt-10 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.12)]">
          <div className="grid lg:grid-cols-12">
            <div className="p-8 sm:p-10 md:p-12 lg:col-span-7">
              <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-primary-soft/90 px-3 py-1.5 ring-1 ring-primary/10">
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2} />
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-strong">
                  Big Resto Bar
                </span>
              </div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.65rem]">
                {t("birthdays.restoBarTitle")}
              </h2>
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600 sm:text-base">
                {t("birthdays.restoBarDesc")}
              </p>
              <ul className="mt-8 space-y-3.5">
                {features.map((key) => (
                  <li
                    key={key}
                    className="flex gap-3.5 rounded-xl border border-slate-100/90 bg-slate-50/80 px-4 py-3.5 transition-colors hover:border-slate-200/90 hover:bg-slate-50"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-200/80">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2} />
                    </div>
                    <span className="pt-0.5 text-[15px] leading-snug text-slate-700">{t(key)}</span>
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_BIRTHDAYS}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-10 inline-flex min-h-12 items-center justify-center gap-2.5 rounded-2xl bg-[#128C7E] px-8 py-3.5 text-[15px] font-semibold text-white shadow-[0_4px_14px_-2px_rgba(18,140,126,0.45)] ring-1 ring-black/5 transition hover:bg-[#0f7a6e] hover:shadow-[0_6px_20px_-4px_rgba(18,140,126,0.5)] active:scale-[0.99]"
              >
                <WhatsAppIcon size={20} className="h-5 w-5" />
                {t("birthdays.inquiryCta")}
              </a>
            </div>

            <div className="relative flex min-h-[200px] flex-col items-center justify-center border-t border-slate-100 bg-linear-to-br from-primary-soft/50 via-slate-50 to-slate-100/90 p-10 lg:col-span-5 lg:min-h-0 lg:border-l lg:border-t-0">
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.35]"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.35) 1px, transparent 0)",
                  backgroundSize: "24px 24px",
                }}
                aria-hidden
              />
              <div className="relative flex flex-col items-center gap-5 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-md ring-1 ring-slate-200/80">
                  <PartyPopper className="h-8 w-8 text-primary" strokeWidth={1.75} />
                </span>
                <span
                  className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/90 shadow-sm ring-1 ring-slate-200/70"
                  aria-hidden
                >
                  <UtensilsCrossed className="h-6 w-6 text-slate-600" strokeWidth={1.75} />
                </span>
                <p className="max-w-[200px] text-[13px] font-medium leading-relaxed text-slate-600">
                  {t("birthdays.asideTagline")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
